import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SignOut, Plus, Trash, Image, Quotes, Envelope } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user, token, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !user) navigate("/admin/login");
  }, [user, authLoading, navigate]);

  if (authLoading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-white">Loading...</div>;
  if (!user) return null;

  const headers = { Authorization: `Bearer ${token}` };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-dashboard">
      <Toaster theme="dark" />
      {/* Header */}
      <div className="border-b border-white/10 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="font-['Cormorant_Garamond'] text-xl text-white">
              DOMINICAN <span className="text-[#FFD700]">ADMIN</span>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-zinc-400 text-sm">{user.email}</span>
            <button
              onClick={() => { logout(); navigate("/admin/login"); }}
              className="flex items-center gap-2 px-4 py-2 text-sm text-zinc-400 hover:text-[#FFD700] border border-white/10 hover:border-[#FFD700] transition-colors"
              data-testid="admin-logout"
            >
              <SignOut size={16} /> Logout
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Tabs defaultValue="gallery">
          <TabsList className="bg-[#1A1A1A] border border-white/10 p-1 mb-8">
            <TabsTrigger value="gallery" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black text-zinc-400 px-6" data-testid="tab-admin-gallery">
              <Image size={16} className="mr-2" /> Gallery
            </TabsTrigger>
            <TabsTrigger value="quotes" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black text-zinc-400 px-6" data-testid="tab-admin-quotes">
              <Envelope size={16} className="mr-2" /> Quotes
            </TabsTrigger>
            <TabsTrigger value="testimonials" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black text-zinc-400 px-6" data-testid="tab-admin-testimonials">
              <Quotes size={16} className="mr-2" /> Testimonials
            </TabsTrigger>
          </TabsList>

          <TabsContent value="gallery"><GalleryManager headers={headers} /></TabsContent>
          <TabsContent value="quotes"><QuotesManager headers={headers} /></TabsContent>
          <TabsContent value="testimonials"><TestimonialsManager headers={headers} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function GalleryManager({ headers }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", category: "capitone", image: "", description: "" });

  const load = useCallback(() => {
    axios.get(`${API}/gallery`).then(r => setItems(r.data));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.title || !form.image) { toast.error("Title and image URL required"); return; }
    try {
      await axios.post(`${API}/admin/gallery`, form, { headers });
      toast.success("Photo added!");
      setForm({ title: "", category: "capitone", image: "", description: "" });
      load();
    } catch { toast.error("Error adding photo"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await axios.delete(`${API}/admin/gallery/${id}`, { headers });
    toast.success("Deleted");
    load();
  };

  const inputCls = "bg-transparent border-white/20 text-white placeholder:text-zinc-500 focus-visible:ring-[#FFD700] h-10";

  return (
    <div data-testid="gallery-manager">
      <h2 className="font-['Cormorant_Garamond'] text-2xl text-white mb-6">Manage Gallery ({items.length} photos)</h2>

      {/* Add form */}
      <form onSubmit={handleAdd} className="bg-[#121212] border border-white/10 p-6 mb-8 space-y-4" data-testid="gallery-add-form">
        <p className="text-[#FFD700] text-xs uppercase tracking-wider flex items-center gap-2"><Plus size={14} /> Add New Photo</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input className={inputCls} placeholder="Title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="gallery-input-title" />
          <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
            <SelectTrigger className={inputCls} data-testid="gallery-select-category"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-white/10 text-white">
              <SelectItem value="capitone">Capitone / Tufted</SelectItem>
              <SelectItem value="acanalado">Acanalado / Channeled</SelectItem>
              <SelectItem value="liso">Liso / Smooth</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Input className={inputCls} placeholder="Image URL (paste direct link)" value={form.image} onChange={e => setForm({...form, image: e.target.value})} data-testid="gallery-input-image" />
        <Textarea className={`${inputCls} min-h-[60px]`} placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="gallery-input-desc" />
        <button type="submit" className="px-6 py-2 bg-[#FFD700] text-black text-sm font-medium uppercase tracking-wider hover:bg-[#E5C100]" data-testid="gallery-add-btn">Add Photo</button>
      </form>

      {/* Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map(item => (
          <div key={item.id} className="bg-[#121212] border border-white/10 overflow-hidden group" data-testid={`admin-gallery-${item.id}`}>
            <div className="h-40 overflow-hidden">
              <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
            </div>
            <div className="p-3 flex justify-between items-start">
              <div>
                <p className="text-white text-sm truncate">{item.title}</p>
                <p className="text-zinc-500 text-xs">{item.category}</p>
              </div>
              <button onClick={() => handleDelete(item.id)} className="text-zinc-500 hover:text-red-400 transition-colors p-1" data-testid={`delete-gallery-${item.id}`}>
                <Trash size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function QuotesManager({ headers }) {
  const [quotes, setQuotes] = useState([]);

  const load = useCallback(() => {
    axios.get(`${API}/admin/quotes`, { headers }).then(r => setQuotes(r.data));
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    await axios.delete(`${API}/admin/quotes/${id}`, { headers });
    toast.success("Quote deleted");
    load();
  };

  return (
    <div data-testid="quotes-manager">
      <h2 className="font-['Cormorant_Garamond'] text-2xl text-white mb-6">Quote Requests ({quotes.length})</h2>
      {quotes.length === 0 ? (
        <p className="text-zinc-500">No quote requests yet.</p>
      ) : (
        <div className="space-y-4">
          {quotes.map(q => (
            <div key={q.id} className="bg-[#121212] border border-white/10 p-6" data-testid={`quote-${q.id}`}>
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="text-white font-medium">{q.name}</p>
                  <p className="text-zinc-400 text-sm">{q.email} | {q.phone}</p>
                </div>
                <button onClick={() => handleDelete(q.id)} className="text-zinc-500 hover:text-red-400 p-1"><Trash size={16} /></button>
              </div>
              <div className="flex gap-3 mb-2 flex-wrap">
                <span className="text-xs px-2 py-1 bg-[#FFD700]/10 text-[#FFD700]">{q.service_type}</span>
                {q.material_type && <span className="text-xs px-2 py-1 bg-white/5 text-zinc-400">{q.material_type}</span>}
                {q.color_preference && <span className="text-xs px-2 py-1 bg-white/5 text-zinc-400">{q.color_preference}</span>}
              </div>
              <p className="text-zinc-300 text-sm font-light">{q.description}</p>
              <p className="text-zinc-600 text-xs mt-2">{q.created_at}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TestimonialsManager({ headers }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", role: "", text: "", rating: 5 });

  const load = useCallback(() => {
    axios.get(`${API}/testimonials`).then(r => setItems(r.data));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.name || !form.text) { toast.error("Name and text required"); return; }
    try {
      await axios.post(`${API}/admin/testimonials`, form, { headers });
      toast.success("Testimonial added!");
      setForm({ name: "", role: "", text: "", rating: 5 });
      load();
    } catch { toast.error("Error"); }
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/admin/testimonials/${id}`, { headers });
    toast.success("Deleted");
    load();
  };

  const inputCls = "bg-transparent border-white/20 text-white placeholder:text-zinc-500 focus-visible:ring-[#FFD700] h-10";

  return (
    <div data-testid="testimonials-manager">
      <h2 className="font-['Cormorant_Garamond'] text-2xl text-white mb-6">Manage Testimonials ({items.length})</h2>

      <form onSubmit={handleAdd} className="bg-[#121212] border border-white/10 p-6 mb-8 space-y-4" data-testid="testimonial-add-form">
        <p className="text-[#FFD700] text-xs uppercase tracking-wider flex items-center gap-2"><Plus size={14} /> Add Testimonial</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input className={inputCls} placeholder="Client name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
          <Input className={inputCls} placeholder="Role (e.g. Restaurant Owner)" value={form.role} onChange={e => setForm({...form, role: e.target.value})} />
        </div>
        <Textarea className={`${inputCls} min-h-[80px]`} placeholder="Testimonial text" value={form.text} onChange={e => setForm({...form, text: e.target.value})} />
        <button type="submit" className="px-6 py-2 bg-[#FFD700] text-black text-sm font-medium uppercase tracking-wider hover:bg-[#E5C100]">Add Testimonial</button>
      </form>

      <div className="space-y-4">
        {items.map(t => (
          <div key={t.id} className="bg-[#121212] border border-white/10 p-6 flex justify-between" data-testid={`admin-testimonial-${t.id}`}>
            <div>
              <p className="text-white italic">"{t.text}"</p>
              <p className="text-[#FFD700] text-sm mt-2">— {t.name}, {t.role}</p>
            </div>
            <button onClick={() => handleDelete(t.id)} className="text-zinc-500 hover:text-red-400 p-1 flex-shrink-0"><Trash size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
